import { createContext, redirect, type RouteObject } from "react-router";
import { deferred } from "@/components/admin/loading";
import { forget, identify } from "./lib/analytics.js";
import { ApiError, type AdminMe, type Api } from "./lib/api.js";
import { AssetsRoute, AssetsSkeleton } from "./routes/assets.jsx";
import { RouteError } from "./routes/error.jsx";
import { Root, Shell } from "./routes/shell.jsx";
import { NoAccessRoute, NoAccessSkeleton, SignInRoute } from "./routes/sign-in.jsx";
import { UserRoute, UserSkeleton } from "./routes/user.jsx";
import { UsersRoute, UsersSkeleton } from "./routes/users.jsx";
import { WorkspaceRoute, WorkspaceSkeleton } from "./routes/workspace.jsx";
import { WorkspacesRoute, WorkspacesSkeleton } from "./routes/workspaces.jsx";

const meContext = createContext<AdminMe>();

/** A list's search term and position come from the URL, so a reload or a shared link shows the same rows. */
const listParams = (request: Request) => {
  const url = new URL(request.url);
  return { search: url.searchParams.get("q") ?? "", offset: Math.max(0, Number(url.searchParams.get("offset") ?? 0) || 0) };
};

/**
 * Routes take the API client as an argument so a test can hand in a fake one.
 * The shell's middleware is the gate: not signed in → /sign-in; signed in but not an administrator →
 * /no-access, which says so and shows the id to grant. Nothing else is requested for them.
 * Page loaders never await (`deferred`): a click switches the page at once and it loads in place.
 * `handle.skeleton` is what a fresh load shows while the gate reads the session.
 */
export function createRoutes(api: Api): RouteObject[] {
  return [
    {
      // React Aria's links navigate through the router, and every page may raise a toast.
      Component: Root,
      children: [
        { path: "/sign-in", loader: () => ({ api }), Component: SignInRoute },
        {
          path: "/no-access",
          // Awaits: who is asking decides whether this page is shown at all.
          loader: async () => {
            const me = await api.me().catch(() => null);
            if (!me) throw redirect("/sign-in");
            if (me.admin) throw redirect("/");
            return { api, me };
          },
          Component: NoAccessRoute,
          HydrateFallback: NoAccessSkeleton,
        },
        {
          path: "/",
          id: "shell",
          // Middleware, not the loader: child loaders run alongside their parent's, and nothing may be
          // requested for someone the gate is about to turn away.
          middleware: [
            async ({ context }) => {
              const me = await api.me().catch((err: unknown) => {
                if (err instanceof ApiError && err.status === 401) {
                  forget();
                  throw redirect("/sign-in");
                }
                throw err;
              });
              identify(me);
              if (!me.admin) throw redirect("/no-access");
              context.set(meContext, me);
            },
          ],
          loader: ({ context }) => ({ api, me: context.get(meContext) }),
          Component: Shell,
          // On a fresh load the shell renders before the gate is through, around the page's skeleton.
          HydrateFallback: Shell,
          ErrorBoundary: RouteError,
          children: [
            { index: true, loader: () => redirect("/users"), handle: { skeleton: UsersSkeleton } },
            { path: "users", loader: ({ request }) => ({ api, data: deferred(api.users(listParams(request))) }), Component: UsersRoute, ErrorBoundary: RouteError, handle: { skeleton: UsersSkeleton } },
            {
              path: "users/:id",
              loader: ({ params }) => ({ api, data: deferred(Promise.all([api.user(params.id ?? ""), api.sessions(params.id ?? "")])) }),
              Component: UserRoute,
              ErrorBoundary: RouteError,
              handle: { skeleton: UserSkeleton },
            },
            { path: "workspaces", loader: ({ request }) => ({ api, data: deferred(api.workspaces(listParams(request))) }), Component: WorkspacesRoute, ErrorBoundary: RouteError, handle: { skeleton: WorkspacesSkeleton } },
            { path: "assets", loader: () => ({ api, data: deferred(api.assets()) }), Component: AssetsRoute, ErrorBoundary: RouteError, handle: { skeleton: AssetsSkeleton } },
            { path: "workspaces/:id", loader: ({ params }) => ({ api, data: deferred(api.workspace(params.id ?? "")) }), Component: WorkspaceRoute, ErrorBoundary: RouteError, handle: { skeleton: WorkspaceSkeleton } },
          ],
        },
      ],
    },
  ];
}
